import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "../styleSheets/messageEdit.css";
import "../styleSheets/buttons.css";
import "../styleSheets/commonElements.css";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Attachment,
  ChangeLogChangeType,
  ChangeLogEntityType,
  Message,
  MessageAttachment,
  MessageRecipient,
  Recipient,
} from "../API";
import {
  type LegacyMessageListItem,
  isMessageEditableByStatus,
} from "../types/message";
import {
  createMessageWithBody,
  deleteMessage,
  deleteMessageAttachment,
  deleteMessageRecipient,
  updateMessageWithBody,
} from "../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { useUser } from "../contexts/UserContext";
import {
  DEFAULT_SCHEDULED_UTC_SLOT,
  FIXED_SCHEDULED_UTC_SLOTS,
  type FixedScheduledUtcSlot,
  combineUtcDateAndSlot,
  convertUtcToLocal,
  getFileTypeIcon,
  getScheduledSlotLocalLabel,
  getScheduledUtcSlotFromDate,
  getTomorrowLocalDateInputValue,
  getUtcDateInputValue,
  capitalizeFirst,
  logToConsole,
} from "../utilities/utilities";
import {
  buildMessageBodySnippet,
  canonicalizeMessageBodyHtml,
  extractMessageBodyPlainText,
  extractMessageBodyPlainTextForEditor,
  flattenMessageBodyToPlainTextHtml,
  plainTextToMessageBodyHtml,
} from "../utilities/messageBodyHtml";
import {
  listMessageAttachments,
  listMessageRecipients,
} from "../graphql/queries";
import SelectRecipients from "./SelectRecipients";
import SelectAttachments from "./SelectAttachments";
import MessageBodyEditor from "./MessageBodyEditor";
import { useQueryClient } from "@tanstack/react-query";
import {
  useRecipients,
  useAttachments,
  useEffectivePlatformLimits,
  useUserPlan,
  useUserSettings,
} from "../graphql/hooks";
import { queryKeys } from "../graphql/queryKeys";
import ConfirmationModal from "./ui/ConfirmationModal";
import BlockingPageLoader from "./ui/BlockingPageLoader";
import { useUnsavedChangesModalGuard } from "../utilities/formValidation";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";
import { TooltipButton } from "../utilities/ToolTipButton";
import {
  getLocalizedPlatformLimitErrorMessage,
  parseStructuredLimitError,
} from "../utilities/platformLimitErrors";
import {
  type ChangeLogEntryInput,
  createChangeRequestId,
  fetchMessageById,
  logChanges,
} from "../graphql/fetchers";

type DeliveryType = "none" | "eol" | "scheduled";
type MessageFieldErrors = {
  title?: string;
  body?: string;
  deliveryType?: string;
  sendOn?: string;
};

type TranslateFn = (key: string) => string;
type RecipientAddBlockedReason =
  | "loading"
  | "max_recipients_per_message_reached"
  | null;
type AttachmentAddBlockedReason =
  | "loading"
  | "max_attachments_per_message_reached"
  | "max_total_attachment_size_per_message_reached"
  | null;

type MessageAuditSnapshot = {
  titleComparable: string;
  bodyComparable: string;
  recipients: string[];
  attachments: string[];
  scheduleSummary: string;
};

type ChangedFields = {
  title?: true;
  body?: true;
  recipients?: {
    old: string[];
    new: string[];
  };
  attachments?: {
    old: string[];
    new: string[];
  };
  schedule?: {
    old: string;
    new: string;
  };
};

function getMessageEditAttachmentSizeValue(
  attachment: Pick<Attachment, "size"> | null | undefined,
): number {
  const size = Number(attachment?.size ?? 0);
  return Number.isFinite(size) ? size : 0;
}

function getRecipientAddBlockedMessage(
  t: TranslateFn,
  reason: RecipientAddBlockedReason,
): string | null {
  switch (reason) {
    case "loading":
      return t("common.loading");
    case "max_recipients_per_message_reached":
      return t("message.errorMessage.maxRecipientsPerMessageReached");
    default:
      return null;
  }
}

function getAttachmentAddBlockedMessage(
  t: TranslateFn,
  reason: AttachmentAddBlockedReason,
): string | null {
  switch (reason) {
    case "loading":
      return t("common.loading");
    case "max_attachments_per_message_reached":
      return t("message.errorMessage.maxAttachmentsPerMessageReached");
    case "max_total_attachment_size_per_message_reached":
      return t("message.errorMessage.maxTotalAttachmentSizePerMessageReached");
    default:
      return null;
  }
}

type EditMessageSource = LegacyMessageListItem | Message | null;

const EMPTY_MESSAGE_AUDIT_SNAPSHOT: MessageAuditSnapshot = {
  titleComparable: "",
  bodyComparable: "",
  recipients: [],
  attachments: [],
  scheduleSummary: "none",
};

const EMPTY_DIRTY_CHECK_SNAPSHOT = JSON.stringify({
  title: "",
  body: "",
  scheduleSummary: getMessageScheduleSummary({
    deliveryType: "none",
    sendOn: "",
    scheduledTimeSlot: DEFAULT_SCHEDULED_UTC_SLOT,
  }),
  recipientIds: "",
  attachmentIds: "",
});

const MESSAGE_EDIT_CHANGE_LOG_METADATA = {
  source: "UI",
  page: "MessageEdit",
  device: "web",
} as const;

const MESSAGE_DELETE_CHANGE_LOG_METADATA = {
  source: "UI",
  action: "delete_confirmed",
} as const;

const MESSAGE_DETAIL_LOAD_TIMEOUT_MS = 15_000;

// Mutating a ref never schedules a React re-render on its own. The
// hydration-latch refs below gate what MessageEdit renders (see
// isEditorHydrationPending), so flipping one from pending -> hydrated must
// force a render even when no other state happens to change in the same
// effect (e.g. the paired setState calls are no-ops because the values
// already match, which React's Object.is bailout then silently skips).
// Guarded to only fire on the one-way false -> true transition so it never
// re-triggers once hydrated.
function markHydrated(
  ref: React.MutableRefObject<boolean>,
  bump: () => void,
) {
  if (!ref.current) {
    ref.current = true;
    bump();
  }
}

function getMessageDeliveryType(message: EditMessageSource): DeliveryType {
  if (!message) return "none";
  if (message.endOfLifeTrigger) return "eol";
  if (message.scheduledDate) return "scheduled";
  return "none";
}

function getMessageBody(message: EditMessageSource): string {
  return message && "body" in message ? (message.body ?? "") : "";
}

function isDetailedMessage(message: EditMessageSource): message is Message {
  return !!message && "body" in message && typeof message.body === "string";
}

function isReadyMessageDetail(message: EditMessageSource): message is Message {
  return (
    isDetailedMessage(message) &&
    getHydratedMessageRecipients(message) !== null &&
    getHydratedMessageAttachments(message) !== null
  );
}

function getEditableMessageBody(
  bodyHtmlOrText: string | null | undefined,
): string {
  return extractMessageBodyPlainTextForEditor(bodyHtmlOrText);
}

function getComparableMessageBodyHtml(
  bodyHtmlOrText: string | null | undefined,
): string {
  return canonicalizeMessageBodyHtml(bodyHtmlOrText);
}

function getMessageScheduleSummary({
  deliveryType = "none",
  sendOn = "",
  scheduledTimeSlot = DEFAULT_SCHEDULED_UTC_SLOT,
}: {
  deliveryType?: DeliveryType;
  sendOn?: string | null;
  scheduledTimeSlot?: FixedScheduledUtcSlot;
}): string {
  if (deliveryType === "eol") {
    return "eol";
  }

  if (deliveryType !== "scheduled") {
    return "none";
  }

  const combinedUtcValue = combineUtcDateAndSlot(
    (sendOn ?? "").trim(),
    scheduledTimeSlot,
  );
  const formattedValue = convertUtcToLocal(combinedUtcValue, "ShowDayLong");

  return formattedValue
    ? `fixed date ${formattedValue}`
    : `fixed date ${(sendOn ?? "").trim() || "unspecified"} ${scheduledTimeSlot}`;
}

function normalizeIdList(ids: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(ids.map((id) => (id ?? "").trim()).filter(Boolean)),
  ).sort();
}

function getWordCount(value: string): number {
  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized ? normalized.split(" ").length : 0;
}

function getMessageSnapshot({
  title,
  body,
  bodyComparable,
  recipients,
  attachments,
  deliveryType = "none",
  sendOn = "",
  scheduledTimeSlot = DEFAULT_SCHEDULED_UTC_SLOT,
}: {
  title?: string | null;
  body?: string | null;
  bodyComparable?: string | null;
  recipients?: Array<{ id: string }>;
  attachments?: Array<{ id: string }>;
  deliveryType?: DeliveryType;
  sendOn?: string | null;
  scheduledTimeSlot?: FixedScheduledUtcSlot;
}): MessageAuditSnapshot {
  return {
    titleComparable: (title ?? "").trim(),
    bodyComparable: bodyComparable ?? getComparableMessageBodyHtml(body ?? ""),
    recipients: normalizeIdList(
      recipients?.map((recipient) => recipient.id) ?? [],
    ),
    attachments: normalizeIdList(
      attachments?.map((attachment) => attachment.id) ?? [],
    ),
    scheduleSummary: getMessageScheduleSummary({
      deliveryType,
      sendOn,
      scheduledTimeSlot,
    }),
  };
}

function getDirtyCheckSnapshot(snapshot: MessageAuditSnapshot): string {
  return JSON.stringify({
    title: snapshot.titleComparable,
    body: snapshot.bodyComparable,
    scheduleSummary: snapshot.scheduleSummary,
    recipientIds: snapshot.recipients.join("|"),
    attachmentIds: snapshot.attachments.join("|"),
  });
}

function getBaselineMessageSnapshot(
  message: EditMessageSource,
): MessageAuditSnapshot | null {
  if (!message || !("body" in message)) {
    return null;
  }

  const recipients = getHydratedMessageRecipients(message);
  const attachments = getHydratedMessageAttachments(message);
  if (recipients === null || attachments === null) {
    return null;
  }

  return getMessageSnapshot({
    title: message.title ?? "",
    bodyComparable: getComparableMessageBodyHtml(message.body ?? ""),
    recipients,
    attachments,
    deliveryType: getMessageDeliveryType(message),
    sendOn: getUtcDateInputValue(message.scheduledDate ?? ""),
    scheduledTimeSlot: getScheduledUtcSlotFromDate(message.scheduledDate ?? ""),
  });
}

function getOpenedMessageBaselineSnapshot(
  sourceMessage: EditMessageSource,
  loadedMessage: Message | null,
): MessageAuditSnapshot | null {
  const bodySource =
    loadedMessage ??
    (sourceMessage && "body" in sourceMessage ? sourceMessage : null);
  const relationSource = sourceMessage ?? loadedMessage;
  const scheduleSource = sourceMessage ?? bodySource;

  if (!bodySource || !relationSource || !scheduleSource) {
    return null;
  }

  const recipients = getHydratedMessageRecipients(relationSource);
  const attachments = getHydratedMessageAttachments(relationSource);
  if (recipients === null || attachments === null) {
    return null;
  }

  return getMessageSnapshot({
    title: relationSource.title ?? bodySource.title ?? "",
    bodyComparable: getComparableMessageBodyHtml(getMessageBody(bodySource)),
    recipients,
    attachments,
    deliveryType: getMessageDeliveryType(scheduleSource),
    sendOn: getUtcDateInputValue(scheduleSource.scheduledDate ?? ""),
    scheduledTimeSlot: getScheduledUtcSlotFromDate(
      scheduleSource.scheduledDate ?? "",
    ),
  });
}

function getChangedFields(
  before: MessageAuditSnapshot,
  after: MessageAuditSnapshot,
): ChangedFields {
  const changedFields: ChangedFields = {};

  if (before.titleComparable !== after.titleComparable) {
    changedFields.title = true;
  }

  if (before.bodyComparable !== after.bodyComparable) {
    changedFields.body = true;
  }

  if (JSON.stringify(before.recipients) !== JSON.stringify(after.recipients)) {
    changedFields.recipients = {
      old: before.recipients,
      new: after.recipients,
    };
  }

  if (
    JSON.stringify(before.attachments) !== JSON.stringify(after.attachments)
  ) {
    changedFields.attachments = {
      old: before.attachments,
      new: after.attachments,
    };
  }

  if (before.scheduleSummary !== after.scheduleSummary) {
    changedFields.schedule = {
      old: before.scheduleSummary,
      new: after.scheduleSummary,
    };
  }

  return changedFields;
}

function getHydratedMessageRecipients(
  message: EditMessageSource,
): Recipient[] | null {
  const items = message?.Recipients?.items;
  if (!Array.isArray(items)) return null;

  const recipients: Recipient[] = [];
  for (const item of items) {
    if (!item) continue;
    if (!item.recipient?.id) {
      return null;
    }
    recipients.push(item.recipient);
  }

  return recipients;
}

function getHydratedMessageAttachments(
  message: EditMessageSource,
): Attachment[] | null {
  const items = message?.Attachments?.items;
  if (!Array.isArray(items)) return null;

  const attachments: Attachment[] = [];
  for (const item of items) {
    if (!item) continue;
    if (!item.attachment?.id) {
      return null;
    }
    attachments.push(item.attachment);
  }

  return attachments;
}

function buildMessageRecipientsSnapshot(
  messageId: string,
  recipients: Recipient[],
): MessageRecipient[] {
  const timestamp = new Date().toISOString();

  return recipients.map(
    (recipient, index) =>
      ({
        __typename: "MessageRecipient",
        id: `local-message-recipient-${messageId}-${recipient.id}-${index}`,
        recipientId: recipient.id,
        messageId,
        recipient,
        message: undefined as unknown as Message,
        createdAt: timestamp,
        updatedAt: timestamp,
      }) as MessageRecipient,
  );
}

function buildMessageAttachmentsSnapshot(
  messageId: string,
  attachments: Attachment[],
): MessageAttachment[] {
  const timestamp = new Date().toISOString();

  return attachments.map(
    (attachment, index) =>
      ({
        __typename: "MessageAttachment",
        id: `local-message-attachment-${messageId}-${attachment.id}-${index}`,
        messageId,
        attachmentId: attachment.id,
        attachment,
        message: undefined as unknown as Message,
        createdAt: timestamp,
        updatedAt: timestamp,
      }) as MessageAttachment,
  );
}

function buildSavedMessageSnapshot({
  baseMessage,
  savedMessage,
  title,
  body,
  bodySnippet,
  scheduledDate,
  endOfLifeTrigger,
  size,
  userId,
  recipients,
  attachments,
}: {
  baseMessage: EditMessageSource;
  savedMessage: Partial<Message> & { id: string };
  title: string;
  body: string;
  bodySnippet: string;
  scheduledDate: string | null;
  endOfLifeTrigger: boolean;
  size: number;
  userId: string;
  recipients: Recipient[];
  attachments: Attachment[];
}): Message {
  const timestamp = new Date().toISOString();
  const messageId = savedMessage.id;

  return {
    __typename: "Message",
    id: messageId,
    title,
    body,
    bodySnippet,
    status: savedMessage.status ?? baseMessage?.status ?? null,
    scheduledDate,
    endOfLifeTrigger,
    size,
    userprofileID: userId,
    Recipients: {
      __typename: "ModelMessageRecipientConnection",
      items: buildMessageRecipientsSnapshot(messageId, recipients),
      nextToken: null,
    },
    Attachments: {
      __typename: "ModelMessageAttachmentConnection",
      items: buildMessageAttachmentsSnapshot(messageId, attachments),
      nextToken: null,
    },
    sentDate: savedMessage.sentDate ?? baseMessage?.sentDate ?? null,
    createdAt: savedMessage.createdAt ?? baseMessage?.createdAt ?? timestamp,
    updatedAt: savedMessage.updatedAt ?? timestamp,
    bodyIv: savedMessage.bodyIv ?? null,
    bodyAuthTag: savedMessage.bodyAuthTag ?? null,
    bodyEncryptedDataKey: savedMessage.bodyEncryptedDataKey ?? null,
  };
}

function MessageEdit() {
  const location = useLocation();
  const navigate = useNavigate();
  const { message: routeMessageId } = useParams<{ message?: string }>();
  const { setGuard } = useNavigationGuard();
  const { t } = useTranslation("common");

  const stateMessage = (location.state as EditMessageSource) || null;
  const messageId = (routeMessageId ?? stateMessage?.id ?? "").trim();
  const sourceMessage =
    !messageId || stateMessage?.id === messageId ? stateMessage : null;
  const isNew = !messageId;
  const { user, isAuthenticated } = useUser();
  const userId = user?.userId ?? "";

  const recipientsQ = useRecipients(userId, isAuthenticated && !!userId);
  const attachmentsQ = useAttachments(userId, isAuthenticated && !!userId);
  const effectivePlatformLimitsQ = useEffectivePlatformLimits(
    userId,
    isAuthenticated && !!userId,
  );
  const planQ = useUserPlan(userId, isAuthenticated && !!userId);
  const settingsQ = useUserSettings(userId, isAuthenticated && !!userId);

  const userRecipients: Recipient[] = recipientsQ.data ?? [];
  const userAttachments: Attachment[] = attachmentsQ.data ?? [];
  const effectivePlatformLimits = effectivePlatformLimitsQ.data;
  const userPlan = planQ.data;
  const minBodyWordCount = settingsQ.data?.minBodyWordCount ?? 25;
  const queryClient = useQueryClient();
  const canUseRichText = !!userPlan && userPlan.type !== "Free";
  const sourceDetailMessage =
    isReadyMessageDetail(sourceMessage) && sourceMessage.id === messageId
      ? sourceMessage
      : null;
  const cachedDetailMessage = useMemo(() => {
    if (isNew || !messageId) {
      return null;
    }

    return (
      queryClient.getQueryData<Message | null>(
        queryKeys.messageDetail(messageId),
      ) ?? null
    );
  }, [isNew, messageId, queryClient]);
  const cachedReadyDetailMessage =
    isReadyMessageDetail(cachedDetailMessage) &&
    cachedDetailMessage.id === messageId
      ? cachedDetailMessage
      : null;
  const initialBodySource =
    sourceDetailMessage ?? cachedReadyDetailMessage ?? sourceMessage;
  const [msg, setMsg] = useState<Message | null>(
    sourceDetailMessage ?? cachedReadyDetailMessage ?? null,
  );
  const [isLoadingMessageDetail, setIsLoadingMessageDetail] = useState(
    !isNew && !(sourceDetailMessage ?? cachedReadyDetailMessage),
  );

  const [isSaveResultModalOpen, setIsSaveResultModalOpen] = useState(false);
  const [saveResultTitle, setSaveResultTitle] = useState("");
  const [saveResultMessage, setSaveResultMessage] = useState("");
  const [saveShouldReturnToPanel, setSaveShouldReturnToPanel] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleteBlockedModalOpen, setIsDeleteBlockedModalOpen] =
    useState(false);
  const [isDeleteSuccessModalOpen, setIsDeleteSuccessModalOpen] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>(
    [],
  );
  const [recipientsDropdownOpen, setRecipientsDropdownOpen] = useState(false);
  const [attachmentsDropdownOpen, setAttachmentsDropdownOpen] = useState(false);
  const [title, setTitle] = useState(initialBodySource?.title ?? "");
  const [bodyHtmlDraft, setBodyHtmlDraft] = useState(
    getComparableMessageBodyHtml(getMessageBody(initialBodySource)),
  );
  const [bodyPlainTextDraft, setBodyPlainTextDraft] = useState(
    getEditableMessageBody(getMessageBody(initialBodySource)),
  );
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(
    getMessageDeliveryType(sourceMessage),
  );
  const [sendOn, setSendOn] = useState<string>(
    getUtcDateInputValue(sourceMessage?.scheduledDate ?? ""),
  );
  const [scheduledTimeSlot, setScheduledTimeSlot] =
    useState<FixedScheduledUtcSlot>(
      getScheduledUtcSlotFromDate(sourceMessage?.scheduledDate ?? ""),
    );
  const minimumScheduledDate = useMemo(
    () => getTomorrowLocalDateInputValue(),
    [],
  );
  const scheduledPreviewValue = useMemo(
    () =>
      deliveryType === "scheduled" && sendOn.trim()
        ? combineUtcDateAndSlot(sendOn, scheduledTimeSlot)
        : "",
    [deliveryType, sendOn, scheduledTimeSlot],
  );
  const [fieldErrors, setFieldErrors] = useState<MessageFieldErrors>({});
  const [formError, setFormError] = useState("");
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(
    isNew ? EMPTY_DIRTY_CHECK_SNAPSHOT : null,
  );
  const [isMessageDetailTimedOut, setIsMessageDetailTimedOut] =
    useState(false);
  const [messageDetailRetryToken, setMessageDetailRetryToken] = useState(0);
  const bodyComparableHtml = useMemo(
    () => getComparableMessageBodyHtml(bodyHtmlDraft),
    [bodyHtmlDraft],
  );
  const loadedMessage = msg?.id === messageId ? msg : null;
  const sourceRecipients = useMemo(
    () => getHydratedMessageRecipients(sourceMessage),
    [sourceMessage],
  );
  const sourceAttachments = useMemo(
    () => getHydratedMessageAttachments(sourceMessage),
    [sourceMessage],
  );
  const loadedRecipients = useMemo(
    () => getHydratedMessageRecipients(loadedMessage),
    [loadedMessage],
  );
  const loadedAttachments = useMemo(
    () => getHydratedMessageAttachments(loadedMessage),
    [loadedMessage],
  );
  const availableDetailMessage =
    loadedMessage ?? sourceDetailMessage ?? cachedReadyDetailMessage;
  const baselineMessage = availableDetailMessage ?? sourceMessage;
  const requiresDraftHydration =
    !isNew && !(sourceDetailMessage ?? cachedReadyDetailMessage);
  const currentMessageId = (
    availableDetailMessage?.id ??
    sourceMessage?.id ??
    messageId
  ).trim();
  const canEditLoadedMessage = isNew || !!availableDetailMessage;
  const recipientSelectionTouchedRef = useRef(false);
  const attachmentSelectionTouchedRef = useRef(false);
  const recipientRelationsHydratedRef = useRef(isNew);
  const attachmentRelationsHydratedRef = useRef(isNew);
  const draftFieldsTouchedRef = useRef(false);
  const draftFieldsHydratedRef = useRef(isNew);
  const auditBaselineSnapshotRef = useRef<MessageAuditSnapshot | null>(
    isNew ? EMPTY_MESSAGE_AUDIT_SNAPSHOT : null,
  );
  const [, forceHydrationRerender] = useReducer((tick: number) => tick + 1, 0);
  const canDeleteMessage = !isNew && currentMessageId.length > 0;
  const isEditLocked = useMemo(
    () =>
      !isNew &&
      !isMessageEditableByStatus(
        availableDetailMessage?.status ?? sourceMessage?.status ?? null,
      ),
    [availableDetailMessage?.status, isNew, sourceMessage?.status],
  );
  const controlsDisabled =
    isLoadingMessageDetail || isSaving || isEditLocked || !canEditLoadedMessage;
  const deleteButtonDisabled =
    controlsDisabled || isDeleting || !canDeleteMessage;
  const messageTitleLabelId = "message-title-label";
  const messageTitleErrorId = "message-title-error";
  const messageBodyLabelId = "message-body-label";
  const messageBodyErrorId = "message-body-error";
  const scheduleHelpId = "message-schedule-help";
  const scheduleErrorId = "message-schedule-error";
  const maxRecipientsPerMessage =
    effectivePlatformLimits?.maxRecipientsPerMessage ?? null;
  const maxAttachmentsPerMessage =
    userPlan?.attachmentsAllowed === 0
      ? 0
      : (effectivePlatformLimits?.maxAttachmentsPerMessage ?? null);
  const maxTotalAttachmentSizePerMessage =
    effectivePlatformLimits?.maxTotalAttachmentSizePerMessage ?? null;
  const estimatedSelectedAttachmentTotal = useMemo(
    () =>
      selectedAttachments.reduce(
        (total, attachment) =>
          total + getMessageEditAttachmentSizeValue(attachment),
        0,
      ),
    [selectedAttachments],
  );
  const resolveRecipientAddBlockedReason =
    (): RecipientAddBlockedReason => {
      if (
        effectivePlatformLimitsQ.isLoading ||
        maxRecipientsPerMessage === null
      ) {
        return "loading";
      }

      if (selectedRecipients.length >= maxRecipientsPerMessage) {
        return "max_recipients_per_message_reached";
      }

      return null;
    };
  const resolveAttachmentAddBlockedReason = (
    candidateAttachment?: Attachment | null,
  ): AttachmentAddBlockedReason => {
    if (
      effectivePlatformLimitsQ.isLoading ||
      maxAttachmentsPerMessage === null ||
      maxTotalAttachmentSizePerMessage === null
    ) {
      return "loading";
    }

    if (selectedAttachments.length >= maxAttachmentsPerMessage) {
      return "max_attachments_per_message_reached";
    }

    if (estimatedSelectedAttachmentTotal >= maxTotalAttachmentSizePerMessage) {
      return "max_total_attachment_size_per_message_reached";
    }

    if (
      candidateAttachment &&
      estimatedSelectedAttachmentTotal +
        getMessageEditAttachmentSizeValue(candidateAttachment) >
        maxTotalAttachmentSizePerMessage
    ) {
      return "max_total_attachment_size_per_message_reached";
    }

    return null;
  };
  const recipientAddBlockedReason = resolveRecipientAddBlockedReason();
  const attachmentAddBlockedReason = resolveAttachmentAddBlockedReason();
  const recipientAddBlockedMessage = getRecipientAddBlockedMessage(
    t,
    recipientAddBlockedReason,
  );
  const attachmentAddBlockedMessage = getAttachmentAddBlockedMessage(
    t,
    attachmentAddBlockedReason,
  );
  // Whether the title/body/deliveryType/sendOn/scheduledTimeSlot draft
  // fields have been hydrated from baselineMessage at least once. This is a
  // one-way latch (see the useLayoutEffect that sets it, and the reset
  // effect keyed on [isNew, messageId]) rather than a live comparison of
  // current draft state against loadedMessage - a live comparison would
  // flip back to "not hydrated" as soon as the user edits any of these
  // fields (since the edited value then differs from loadedMessage's
  // original value), which would re-trigger isEditorHydrationPending and
  // replace the whole form with BlockingPageLoader mid-edit.
  const isWaitingForDraftHydration =
    requiresDraftHydration &&
    !!loadedMessage &&
    !draftFieldsHydratedRef.current;
  const canHydrateRecipientsFromLoadedMessage =
    !isNew && sourceRecipients === null && loadedRecipients !== null;
  const canHydrateAttachmentsFromLoadedMessage =
    !isNew && sourceAttachments === null && loadedAttachments !== null;
  const canInitializeOpenedBaseline =
    !isNew &&
    getOpenedMessageBaselineSnapshot(sourceMessage, loadedMessage) !== null;
  const isEditorHydrationPending =
    !isNew &&
    !!loadedMessage &&
    (!!isWaitingForDraftHydration ||
      (canHydrateRecipientsFromLoadedMessage &&
        !recipientRelationsHydratedRef.current) ||
      (canHydrateAttachmentsFromLoadedMessage &&
        !attachmentRelationsHydratedRef.current) ||
      (canInitializeOpenedBaseline && initialSnapshot === null));

  const currentAuditSnapshot = useMemo(
    () =>
      getMessageSnapshot({
        title,
        bodyComparable: bodyComparableHtml,
        recipients: selectedRecipients,
        attachments: selectedAttachments,
        deliveryType,
        sendOn,
        scheduledTimeSlot,
      }),
    [
      title,
      bodyComparableHtml,
      deliveryType,
      sendOn,
      scheduledTimeSlot,
      selectedRecipients,
      selectedAttachments,
    ],
  );

  const currentSnapshot = useMemo(
    () => getDirtyCheckSnapshot(currentAuditSnapshot),
    [currentAuditSnapshot],
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!isNew) {
      if (!canEditLoadedMessage || !loadedMessage) {
        return false;
      }

      if (!recipientRelationsHydratedRef.current) {
        return false;
      }

      if (!attachmentRelationsHydratedRef.current) {
        return false;
      }

      if (initialSnapshot === null) {
        return false;
      }
    }

    return currentSnapshot !== initialSnapshot;
  }, [
    isNew,
    canEditLoadedMessage,
    loadedMessage,
    currentSnapshot,
    initialSnapshot,
  ]);

  const {
    isPromptOpen,
    requestNavigation,
    confirmNavigation,
    cancelNavigation,
  } = useUnsavedChangesModalGuard(hasUnsavedChanges);

  useEffect(() => {
    let isMounted = true;

    if (isNew || !messageId) {
      setMsg(null);
      setIsLoadingMessageDetail(false);
      return () => {
        isMounted = false;
      };
    }

    if (sourceDetailMessage?.body !== undefined) {
      setMsg(sourceDetailMessage);
      setIsLoadingMessageDetail(false);
      queryClient.setQueryData(
        queryKeys.messageDetail(messageId),
        sourceDetailMessage,
      );
      return () => {
        isMounted = false;
      };
    }

    if (cachedReadyDetailMessage) {
      setMsg(cachedReadyDetailMessage);
      setIsLoadingMessageDetail(false);
      return () => {
        isMounted = false;
      };
    }

    setMsg((current) => (current?.id === messageId ? current : null));
    setIsLoadingMessageDetail(true);
    void fetchMessageById(messageId)
      .then((fullMessage) => {
        if (!isMounted) return;
        setMsg(fullMessage);
        if (fullMessage?.id) {
          queryClient.setQueryData(
            queryKeys.messageDetail(fullMessage.id),
            fullMessage,
          );
        }
      })
      .catch((error) => {
        logToConsole("error", "Error loading message details:", error);
        if (isMounted) {
          setFormError(t("errorMessage.failedLoad"));
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingMessageDetail(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    cachedReadyDetailMessage,
    isNew,
    messageId,
    messageDetailRetryToken,
    queryClient,
    sourceDetailMessage,
    t,
  ]);

  useLayoutEffect(() => {
    if (!baselineMessage) {
      if (isNew) {
        setTitle("");
        setBodyHtmlDraft("");
        setBodyPlainTextDraft("");
        setDeliveryType("none");
        setSendOn("");
        setScheduledTimeSlot(DEFAULT_SCHEDULED_UTC_SLOT);
      }
      return;
    }

    if (draftFieldsTouchedRef.current) {
      return;
    }

    setTitle(baselineMessage.title ?? "");
    const baselineBody = getMessageBody(baselineMessage);
    setBodyHtmlDraft(getComparableMessageBodyHtml(baselineBody));
    setBodyPlainTextDraft(getEditableMessageBody(baselineBody));
    setDeliveryType(getMessageDeliveryType(baselineMessage));
    setSendOn(getUtcDateInputValue(baselineMessage.scheduledDate ?? ""));
    setScheduledTimeSlot(
      getScheduledUtcSlotFromDate(baselineMessage.scheduledDate ?? ""),
    );
    markHydrated(draftFieldsHydratedRef, forceHydrationRerender);
  }, [baselineMessage, isNew]);

  useEffect(() => {
    setGuard(hasUnsavedChanges ? requestNavigation : null);
    return () => setGuard(null);
  }, [hasUnsavedChanges, requestNavigation, setGuard]);

  useEffect(() => {
    if (deliveryType !== "scheduled") {
      setSendOn("");
      setFieldErrors((prev) => ({
        ...prev,
        sendOn: undefined,
      }));
    }
  }, [deliveryType]);

  useEffect(() => {
    // Scoped to [isNew, messageId] only (not sourceMessage/sourceRecipients/
    // sourceAttachments like the effect below) so this resets only on a
    // genuine navigation to a different message - not merely because the
    // route-state object for the *same* message was refreshed, which must
    // not clear an in-progress, untouched-by-save edit.
    draftFieldsTouchedRef.current = false;
    draftFieldsHydratedRef.current = isNew;
  }, [isNew, messageId]);

  useEffect(() => {
    recipientSelectionTouchedRef.current = false;
    recipientRelationsHydratedRef.current = isNew;
    attachmentSelectionTouchedRef.current = false;
    attachmentRelationsHydratedRef.current = isNew;
    setInitialSnapshot(isNew ? EMPTY_DIRTY_CHECK_SNAPSHOT : null);
    auditBaselineSnapshotRef.current = isNew
      ? EMPTY_MESSAGE_AUDIT_SNAPSHOT
      : null;

    if (isNew) {
      setSelectedRecipients([]);
      setSelectedAttachments([]);
      return;
    }

    if (sourceRecipients !== null) {
      markHydrated(recipientRelationsHydratedRef, forceHydrationRerender);
      setSelectedRecipients(sourceRecipients);
    } else {
      setSelectedRecipients([]);
    }

    if (sourceAttachments !== null) {
      markHydrated(attachmentRelationsHydratedRef, forceHydrationRerender);
      setSelectedAttachments(sourceAttachments);
    } else {
      setSelectedAttachments([]);
    }
  }, [isNew, messageId, sourceMessage, sourceAttachments, sourceRecipients]);

  useEffect(() => {
    if (isNew) return;

    if (sourceRecipients !== null) {
      return;
    }

    if (loadedRecipients === null) return;

    markHydrated(recipientRelationsHydratedRef, forceHydrationRerender);
    if (!recipientSelectionTouchedRef.current) {
      setSelectedRecipients(loadedRecipients);
    }
  }, [isNew, loadedRecipients, sourceRecipients]);

  useEffect(() => {
    if (isNew) return;

    if (sourceAttachments !== null) {
      return;
    }

    if (loadedAttachments === null) return;

    markHydrated(attachmentRelationsHydratedRef, forceHydrationRerender);
    if (!attachmentSelectionTouchedRef.current) {
      setSelectedAttachments(loadedAttachments);
    }
  }, [isNew, loadedAttachments, sourceAttachments]);

  useEffect(() => {
    if (isNew || initialSnapshot !== null) {
      return;
    }

    const baselineSnapshot = getOpenedMessageBaselineSnapshot(
      sourceMessage,
      loadedMessage,
    );
    if (!baselineSnapshot) {
      return;
    }

    auditBaselineSnapshotRef.current = baselineSnapshot;
    setInitialSnapshot(getDirtyCheckSnapshot(baselineSnapshot));
  }, [isNew, initialSnapshot, sourceMessage, loadedMessage]);

  useEffect(() => {
    const isWaitingForMessageDetail =
      !isNew && (isLoadingMessageDetail || isEditorHydrationPending);

    if (!isWaitingForMessageDetail) {
      setIsMessageDetailTimedOut(false);
      return;
    }

    const timerId = window.setTimeout(() => {
      setIsMessageDetailTimedOut(true);
    }, MESSAGE_DETAIL_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timerId);
  }, [
    isNew,
    isLoadingMessageDetail,
    isEditorHydrationPending,
    messageId,
    messageDetailRetryToken,
  ]);

  const lastEditedLabel = useMemo(() => {
    const ts: any =
      (baselineMessage as any)?.updatedAt ??
      (baselineMessage as any)?.createdAt ??
      null;
    if (!ts) return null;
    try {
      return convertUtcToLocal(ts, "ShowDayLong");
    } catch {
      return ts;
    }
  }, [baselineMessage]);

  const addRecipient = (recipient: Recipient) => {
    if (controlsDisabled) {
      return;
    }

    const blockedReason = resolveRecipientAddBlockedReason();
    if (blockedReason) {
      setFormError(getRecipientAddBlockedMessage(t, blockedReason) ?? "");
      return;
    }

    recipientSelectionTouchedRef.current = true;
    setFormError("");
    setSelectedRecipients((prev) =>
      prev.some((r) => r.id === recipient.id) ? prev : [...prev, recipient],
    );
  };
  const removeRecipient = (recipientId: string) => {
    recipientSelectionTouchedRef.current = true;
    setSelectedRecipients((prev) => prev.filter((r) => r.id !== recipientId));
  };
  const addAttachment = (attachment: Attachment) => {
    if (controlsDisabled) {
      return;
    }

    const blockedReason = resolveAttachmentAddBlockedReason(attachment);
    if (blockedReason) {
      setFormError(getAttachmentAddBlockedMessage(t, blockedReason) ?? "");
      return;
    }

    attachmentSelectionTouchedRef.current = true;
    setFormError("");
    setSelectedAttachments((prev) =>
      prev.some((a) => a.id === attachment.id) ? prev : [...prev, attachment],
    );
  };
  const removeAttachment = (attachmentId: string) => {
    attachmentSelectionTouchedRef.current = true;
    setSelectedAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  const handleCancel = () => {
    requestNavigation(() => navigate("/Panel?selectedTab=legacyMessages"));
  };

  const handleRetryMessageDetailLoad = () => {
    setIsMessageDetailTimedOut(false);
    setFormError("");
    setMessageDetailRetryToken((token) => token + 1);
    void recipientsQ.refetch();
    void attachmentsQ.refetch();
  };

  const handleConfirmDiscardChanges = () => {
    if (hasUnsavedChanges && !isNew && currentMessageId && initialSnapshot) {
      void logChanges([
        {
          requestId: createChangeRequestId(),
          entityType: ChangeLogEntityType.MESSAGE,
          entityId: currentMessageId,
          changeType: ChangeLogChangeType.ABANDON,
          metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
          userId,
        },
      ]);
    }

    confirmNavigation();
  };

  const handleDeleteMessage = () => {
    if (deleteButtonDisabled) {
      return;
    }

    setFormError("");
    setIsDeleteBlockedModalOpen(false);
    setIsDeleteSuccessModalOpen(false);
    setIsDeleteConfirmOpen(true);
  };

  const handleKeepDeleteMessage = () => {
    if (isDeleting) {
      return;
    }

    setIsDeleteConfirmOpen(false);
  };

  const handleDismissDeleteBlockedModal = () => {
    setIsDeleteBlockedModalOpen(false);
  };

  const handleAcknowledgeDeleteSuccess = () => {
    setIsDeleteSuccessModalOpen(false);
    navigate("/Panel?selectedTab=legacyMessages");
  };

  const handleConfirmDeleteMessage = async () => {
    if (isDeleting || !canDeleteMessage) {
      return;
    }

    setFormError("");

    if (selectedRecipients.length > 0) {
      setIsDeleteConfirmOpen(false);
      setIsDeleteBlockedModalOpen(true);
      return;
    }

    const client = generateClient();
    setIsDeleting(true);

    try {
      const existingRecipientLinksResult: any = await client.graphql({
        query: listMessageRecipients,
        variables: { filter: { messageId: { eq: currentMessageId } } },
      });
      const existingRecipientLinks =
        existingRecipientLinksResult.data?.listMessageRecipients?.items ?? [];

      for (const link of existingRecipientLinks) {
        if (link?.id) {
          await client.graphql({
            query: deleteMessageRecipient,
            variables: { input: { id: link.id } },
          });
        }
      }

      const existingAttachmentLinksResult: any = await client.graphql({
        query: listMessageAttachments,
        variables: { filter: { messageId: { eq: currentMessageId } } },
      });
      const existingAttachmentLinks =
        existingAttachmentLinksResult.data?.listMessageAttachments?.items ?? [];

      for (const link of existingAttachmentLinks) {
        if (link?.id) {
          await client.graphql({
            query: deleteMessageAttachment,
            variables: { input: { id: link.id } },
          });
        }
      }

      await client.graphql({
        query: deleteMessage,
        variables: { input: { id: currentMessageId } },
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.legacyMessages(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.recipients(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.attachments(userId),
        }),
      ]);

      void logChanges([
        {
          requestId: createChangeRequestId(),
          entityType: ChangeLogEntityType.MESSAGE,
          entityId: currentMessageId,
          changeType: ChangeLogChangeType.DELETE,
          metadata: MESSAGE_DELETE_CHANGE_LOG_METADATA,
          userId,
        },
      ]);

      setFormError("");
      setIsDeleteConfirmOpen(false);
      setIsDeleteBlockedModalOpen(false);
      setIsDeleteSuccessModalOpen(true);
    } catch (error) {
      logToConsole("error", "Error deleting message:", error);
      setFormError(t("message.errorMessage.messageDeleteError"));
      setIsDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const validateMessageData = async (): Promise<boolean> => {
    const errors: MessageFieldErrors = {};
    const currentBodyText = extractMessageBodyPlainText(
      canUseRichText ? bodyHtmlDraft : bodyPlainTextDraft,
    );
    if (!title.trim()) errors.title = t("message.validation.titleRequired");
    if (!currentBodyText.trim()) {
      errors.body = t("message.validation.bodyRequired");
    } else if (getWordCount(currentBodyText) < minBodyWordCount) {
      errors.body = t("message.errorMessage.minBodyLengthError", {
        number: minBodyWordCount,
      });
    }
    if (deliveryType === "scheduled" && !sendOn.trim()) {
      errors.sendOn = t("message.validation.scheduleRequired");
    } else if (
      deliveryType === "scheduled" &&
      sendOn.trim() &&
      sendOn < minimumScheduledDate
    ) {
      errors.sendOn = t("message.validation.scheduleTooSoon");
    }
    if (deliveryType !== "eol" && deliveryType !== "scheduled")
      errors.deliveryType = t("message.validation.deliveryRequired");

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError(t("message.validation.formErrors"));
      return false;
    }
    return true;
  };

  const handleSaveMessage = async () => {
    if (controlsDisabled) {
      return;
    }

    // Defense-in-depth: as currently wired, isEditorHydrationPending (via
    // canInitializeOpenedBaseline, which itself requires both relations to
    // be hydrated before initialSnapshot can be set) already keeps the page
    // behind BlockingPageLoader - with no Save button rendered at all -
    // whenever relations haven't hydrated, so this branch cannot currently
    // be reached through normal UI interaction. It's kept as a hard stop in
    // case that coupling ever changes (e.g. the loading gate's conditions
    // are relaxed independently of this one), rather than relying solely on
    // the loader to prevent a save with incomplete relation data - which is
    // exactly the failure shape (saving before related state has loaded)
    // this project has been burned by before.
    if (
      !isNew &&
      (!recipientRelationsHydratedRef.current ||
        !attachmentRelationsHydratedRef.current)
    ) {
      setFormError(t("message.errorMessage.messageRelationsNotLoaded"));
      return;
    }

    if (!hasUnsavedChanges) {
      setSaveShouldReturnToPanel(true);
      setSaveResultTitle(t("errorMessage.noChangesTitle"));
      setSaveResultMessage(t("errorMessage.noChangesToSave"));
      setIsSaveResultModalOpen(true);
      return;
    }

    let schdDate: string | null = null;
    let endOfLifeTrigger = false;

    if (deliveryType === "eol") {
      endOfLifeTrigger = true;
      schdDate = null;
    }

    if (deliveryType === "scheduled") {
      if (sendOn) {
        schdDate = combineUtcDateAndSlot(sendOn, scheduledTimeSlot) || null;
      } else {
        schdDate = null;
      }
    }

    if (!(await validateMessageData())) return;
    const normalizedBody = canUseRichText
      ? canonicalizeMessageBodyHtml(bodyHtmlDraft)
      : flattenMessageBodyToPlainTextHtml(bodyPlainTextDraft);
    const bodySnippet = buildMessageBodySnippet(normalizedBody);
    const bodySize = extractMessageBodyPlainText(normalizedBody).length;
    const afterAuditSnapshot = getMessageSnapshot({
      title,
      bodyComparable: getComparableMessageBodyHtml(normalizedBody),
      recipients: selectedRecipients,
      attachments: selectedAttachments,
      deliveryType,
      sendOn,
      scheduledTimeSlot,
    });
    const client = generateClient();
    setIsSaving(true);
    try {
      if (!isNew) {
        const existingMessageId = loadedMessage?.id ?? messageId;
        if (!existingMessageId) {
          setFormError(t("errorMessage.failedLoad"));
          return;
        }

        const input = {
          id: existingMessageId,
          title: title,
          body: normalizedBody,
          bodySnippet,
          scheduledDate: schdDate,
          endOfLifeTrigger: endOfLifeTrigger,
          size: bodySize,
          userprofileID: userId,
          recipientIds: selectedRecipients
            .map((recipient) => recipient?.id ?? "")
            .filter(Boolean),
          attachmentIds: selectedAttachments
            .map((attachment) => attachment?.id ?? "")
            .filter(Boolean),
        };

        const beforeAuditSnapshot = (auditBaselineSnapshotRef.current ??
          getBaselineMessageSnapshot(baselineMessage))!;

        const result = await client.graphql({
          query: updateMessageWithBody,
          variables: { input },
        });
        const updatedMessage = (result as any).data?.updateMessageWithBody;
        logToConsole("log", "Updated message:", updatedMessage);

        const savedMessage = buildSavedMessageSnapshot({
          baseMessage: baselineMessage,
          savedMessage: updatedMessage ?? { id: existingMessageId },
          title,
          body: normalizedBody,
          bodySnippet,
          scheduledDate: schdDate,
          endOfLifeTrigger,
          size: bodySize,
          userId,
          recipients: selectedRecipients,
          attachments: selectedAttachments,
        });

        setMsg(savedMessage);
        queryClient.setQueryData(
          queryKeys.messageDetail(savedMessage.id),
          savedMessage,
        );
        auditBaselineSnapshotRef.current = afterAuditSnapshot;
        setInitialSnapshot(getDirtyCheckSnapshot(afterAuditSnapshot));
        setBodyHtmlDraft(normalizedBody);
        setBodyPlainTextDraft(getEditableMessageBody(normalizedBody));

        const updateRequestId = createChangeRequestId();
        const updateAuditEntries: ChangeLogEntryInput[] = [
          {
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.MESSAGE,
            entityId: existingMessageId,
            changeType: ChangeLogChangeType.UPDATE,
            metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
            userId,
          },
        ];

        const changedFields = getChangedFields(
          beforeAuditSnapshot,
          afterAuditSnapshot,
        );

        if (changedFields.title) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.MESSAGE,
            entityId: existingMessageId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "title",
            oldValue: null,
            newValue: null,
            metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.body) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.MESSAGE,
            entityId: existingMessageId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "body",
            oldValue: null,
            newValue: null,
            metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.recipients) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.MESSAGE,
            entityId: existingMessageId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "recipients",
            oldValue: changedFields.recipients.old,
            newValue: changedFields.recipients.new,
            metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.attachments) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.MESSAGE,
            entityId: existingMessageId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "attachments",
            oldValue: changedFields.attachments.old,
            newValue: changedFields.attachments.new,
            metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
            userId,
          });
        }

        if (changedFields.schedule) {
          updateAuditEntries.push({
            requestId: updateRequestId,
            entityType: ChangeLogEntityType.MESSAGE,
            entityId: existingMessageId,
            changeType: ChangeLogChangeType.UPDATE,
            fieldName: "schedule",
            oldValue: changedFields.schedule.old,
            newValue: changedFields.schedule.new,
            metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
            userId,
          });
        }

        void logChanges(updateAuditEntries);

        if (routeMessageId !== existingMessageId) {
          navigate(`/MessageEdit/${existingMessageId}`, {
            replace: true,
            state: savedMessage,
          });
        }
      } else {
        const input = {
          title: title,
          body: normalizedBody,
          bodySnippet,
          scheduledDate: schdDate,
          endOfLifeTrigger: endOfLifeTrigger,
          size: bodySize,
          userprofileID: userId,
          sentDate: null,
          recipientIds: selectedRecipients
            .map((recipient) => recipient?.id ?? "")
            .filter(Boolean),
          attachmentIds: selectedAttachments
            .map((attachment) => attachment?.id ?? "")
            .filter(Boolean),
        };

        const result: any = await client.graphql({
          query: createMessageWithBody,
          variables: { input },
        });
        const createdMessage = result.data?.createMessageWithBody;
        const newId = createdMessage?.id;
        logToConsole("log", "New message:", createdMessage);

        if (!newId) {
          throw new Error("Message create did not return an id.");
        }

        const savedMessage = buildSavedMessageSnapshot({
          baseMessage: null,
          savedMessage: createdMessage,
          title,
          body: normalizedBody,
          bodySnippet,
          scheduledDate: schdDate,
          endOfLifeTrigger,
          size: bodySize,
          userId,
          recipients: selectedRecipients,
          attachments: selectedAttachments,
        });

        setMsg(savedMessage);
        queryClient.setQueryData(
          queryKeys.messageDetail(savedMessage.id),
          savedMessage,
        );
        auditBaselineSnapshotRef.current = afterAuditSnapshot;
        setInitialSnapshot(getDirtyCheckSnapshot(afterAuditSnapshot));
        setBodyHtmlDraft(normalizedBody);
        setBodyPlainTextDraft(getEditableMessageBody(normalizedBody));
        void logChanges([
          {
            requestId: createChangeRequestId(),
            entityType: ChangeLogEntityType.MESSAGE,
            entityId: newId,
            changeType: ChangeLogChangeType.CREATE,
            metadata: MESSAGE_EDIT_CHANGE_LOG_METADATA,
            userId,
          },
        ]);
        navigate(`/MessageEdit/${newId}`, {
          replace: true,
          state: savedMessage,
        });
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.legacyMessages(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.recipients(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.attachments(userId),
        }),
      ]);

      setFieldErrors({});
      setFormError("");
      setSaveShouldReturnToPanel(true);
      setSaveResultTitle(t("common.saveWindowTitle"));
      setSaveResultMessage(t("message.onScreenMessage.messageSavedSuccess"));
      setIsSaveResultModalOpen(true);
    } catch (error) {
      logToConsole("error", "Error saving message:", error);
      const parsedSaveError = parseStructuredLimitError(error);
      const backendSaveMessage = getLocalizedPlatformLimitErrorMessage(
        t,
        error,
        t("message.errorMessage.messageSaveError"),
      );

      setFormError(backendSaveMessage);
      setSaveShouldReturnToPanel(false);
      setSaveResultTitle(
        parsedSaveError?.code || parsedSaveError?.message
          ? t("common.saveWindowTitle")
          : t("message.errorMessage.messageSaveFailedTitle"),
      );
      setSaveResultMessage(backendSaveMessage);
      setIsSaveResultModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };
  const handleContinueEditingAfterSave = () => {
    setIsSaveResultModalOpen(false);
  };

  const handleCloseAfterSave = () => {
    setIsSaveResultModalOpen(false);

    if (saveShouldReturnToPanel) {
      navigate("/Panel?selectedTab=legacyMessages");
    }
  };

  if (!isNew && !isLoadingMessageDetail && !msg && !sourceMessage) {
    return <Navigate to="/Panel?selectedTab=legacyMessages" replace />;
  }

  if (!isNew && (isLoadingMessageDetail || isEditorHydrationPending)) {
    if (isMessageDetailTimedOut) {
      return (
        <BlockingPageLoader
          message={t("message.errorMessage.messageLoadTimeout")}
          isError
          retryLabel={t("common.retry")}
          exitLabel={t("common.cancel")}
          onRetry={handleRetryMessageDetailLoad}
          onExit={handleCancel}
        />
      );
    }
    return <BlockingPageLoader message={t("message.loadingData")} />;
  }

  if (isSaving) {
    return <BlockingPageLoader message={t("common.saving")} />;
  }

  if (isDeleting) {
    return <BlockingPageLoader message={t("common.deleting")} />;
  }

  return (
    <div className="message-edit-page">
      <div className="msg-topbar">
        <div className="msg-topbar__section">
          <div className="msg-topbar__container">
            <div className="msg-topbar__row">
              <button
                id="cancel-btn"
                type="button"
                className="btn btn--gray btn--size-md btn--icon-left"
                onClick={handleCancel}
              >
                <div className="btn__icon icon-close_lrg" aria-hidden="true" />
                {t("common.cancel")}
              </button>

              <div className="msg-topbar__title">
                {t("message.legacyMessage")}
              </div>

              <div className="msg-topbar__actions">
                <button
                  id="save-btn"
                  type="button"
                  className="btn btn--green btn--size-md btn--icon-left"
                  onClick={handleSaveMessage}
                  disabled={controlsDisabled}
                >
                  <span className="btn__icon icon-check" aria-hidden="true" />
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className="msg-topbar__meta">
              {lastEditedLabel
                ? `${t("common.lastEditedOn")}: ${lastEditedLabel}`
                : ""}
            </div>

            <div className="msg-topbar__titleBelow">
              <div className="msg-topbar__titleBelowMain">
                {t("message.legacyMessage")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="msg-content">
        {formError ? <div className="msg-formError">{formError}</div> : null}
        {isEditLocked ? (
          <div className="msg-formError">
            {t("message.onScreenMessage.processedMessagesNoEdit")}
          </div>
        ) : null}
        <div className="msg-card">
          <div
            className={`msg-field msg-field--title${fieldErrors.title ? " msg-field--error" : ""}`}
          >
            <label className="msg-field__label" htmlFor="message-title">
              <div className="msg-field__labelText" id={messageTitleLabelId}>
                {t("message.title")}
              </div>
            </label>
            <div className="msg-field__control">
              <input
                id="message-title"
                className="msg-input"
                type="text"
                value={title}
                placeholder={t("message.onScreenMessage.titlePlaceHolder")}
                disabled={controlsDisabled}
                aria-invalid={!!fieldErrors.title}
                aria-describedby={
                  fieldErrors.title ? messageTitleErrorId : undefined
                }
                onChange={(e) => {
                  draftFieldsTouchedRef.current = true;
                  setTitle(e.target.value);
                  setFormError("");
                  if (fieldErrors.title)
                    setFieldErrors((prev) => ({ ...prev, title: undefined }));
                }}
              />
            </div>
          </div>
          {fieldErrors.title ? (
            <p
              id={messageTitleErrorId}
              className="msg-fieldError"
              aria-live="polite"
            >
              {fieldErrors.title}
            </p>
          ) : null}

          <div className="msg-field msg-field--recipients">
            <div className="msg-field__label">
              <div className="msg-field__labelText">
                {capitalizeFirst(t("recipient.recipients"))}
              </div>
            </div>

            <div className="msg-field__inline">
              <div className="msg-dropdownAnchor">
                <TooltipButton
                  id="recipients-dropdown"
                  label={
                    <>
                      <span
                        className="btn__icon icon-plus red"
                        aria-hidden="true"
                      />
                      {t("common.add")}
                    </>
                  }
                  tooltip={recipientAddBlockedMessage ?? undefined}
                  disabled={controlsDisabled || recipientAddBlockedReason !== null}
                  onClick={() => setRecipientsDropdownOpen(true)}
                  buttonClassName="btn btn--white btn--size-md btn--icon-left"
                />

                <SelectRecipients
                  isOpen={recipientsDropdownOpen}
                  setIsOpen={setRecipientsDropdownOpen}
                  userRecipients={userRecipients}
                  selectedRecipients={selectedRecipients}
                  onAddRecipient={addRecipient}
                />
              </div>

              <div className="msg-selected msg-selected--fill">
                <div className="msg-chips" aria-label="Selected recipients">
                  {selectedRecipients.length === 0 ? (
                    <span className="msg-hint">
                      {t("recipient.onScreenMessage.noRecipients")}
                    </span>
                  ) : (
                    selectedRecipients.map((r) => {
                      const label =
                        (r as any)?.name ||
                        [
                          ((r as any)?.firstName ?? "").trim(),
                          ((r as any)?.lastName ?? "").trim(),
                        ]
                          .filter(Boolean)
                          .join(" ") ||
                        (r as any)?.email ||
                        r.id;

                      return (
                        <div key={r.id} className="msg-chip btn--dark-green">
                          <span className="msg-chip__text">{label}</span>
                          <button
                            id={`recipient-${r.id}-remove-btn`}
                            type="button"
                            className="msg-chip__remove"
                            onClick={() => removeRecipient(r.id)}
                            aria-label={`${t("common.remove")} ${label}`}
                            title={t("common.remove")}
                            disabled={controlsDisabled}
                          >
                            &times;
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className={`msg-editor${fieldErrors.body ? " msg-editor--error" : ""}`}
          >
            {canUseRichText ? (
              <MessageBodyEditor
                label={
                  <div className="msg-field__labelText">
                    {capitalizeFirst(t("message.message"))}
                  </div>
                }
                labelId={messageBodyLabelId}
                descriptionId={fieldErrors.body ? messageBodyErrorId : undefined}
                invalid={!!fieldErrors.body}
                value={bodyHtmlDraft}
                disabled={controlsDisabled}
                placeholder={t("message.onScreenMessage.messagePlaceHolder")}
                onChange={(html) => {
                  draftFieldsTouchedRef.current = true;
                  setBodyHtmlDraft(canonicalizeMessageBodyHtml(html));
                  setFormError("");
                  if (fieldErrors.body) {
                    setFieldErrors((prev) => ({ ...prev, body: undefined }));
                  }
                }}
              />
            ) : (
              <>
                <div className="msg-editor__header">
                  <div className="msg-editor__label" id={messageBodyLabelId}>
                    <div className="msg-field__labelText">
                      {capitalizeFirst(t("message.message"))}
                    </div>
                  </div>
                </div>

                <div className="msg-editor__body">
                  <textarea
                    id="message-body"
                    className="msg-textarea"
                    value={bodyPlainTextDraft}
                    disabled={controlsDisabled}
                    aria-labelledby={messageBodyLabelId}
                    aria-invalid={!!fieldErrors.body}
                    aria-describedby={
                      fieldErrors.body ? messageBodyErrorId : undefined
                    }
                    onChange={(e) => {
                      draftFieldsTouchedRef.current = true;
                      setBodyPlainTextDraft(e.target.value);
                      setBodyHtmlDraft(
                        plainTextToMessageBodyHtml(e.target.value),
                      );
                      setFormError("");
                      if (fieldErrors.body)
                        setFieldErrors((prev) => ({
                          ...prev,
                          body: undefined,
                        }));
                    }}
                    placeholder={t(
                      "message.onScreenMessage.messagePlaceHolder",
                    )}
                  />
                </div>
              </>
            )}
          </div>
          {fieldErrors.body ? (
            <p
              id={messageBodyErrorId}
              className="msg-fieldError"
              aria-live="polite"
            >
              {fieldErrors.body}
            </p>
          ) : null}

          <div className="msg-split">
            <div className="msg-panel msg-panel--attachments">
              <div className="msg-panel__header">
                <div className="msg-field__labelText">
                  {capitalizeFirst(t("file.attachments"))}
                </div>
              </div>

              <div className="msg-panel__body">
                <div className="msg-dropdownAnchor">
                  <TooltipButton
                    id="attachments-dropdown"
                    label={
                      <>
                        <span
                          className="btn__icon icon-plus red"
                          aria-hidden="true"
                        />
                        {t("common.add")}
                      </>
                    }
                    tooltip={attachmentAddBlockedMessage ?? undefined}
                    disabled={
                      controlsDisabled || attachmentAddBlockedReason !== null
                    }
                    onClick={() => setAttachmentsDropdownOpen(true)}
                    buttonClassName="btn btn--white btn--size-md btn--icon-left"
                  />

                  <SelectAttachments
                    isOpen={attachmentsDropdownOpen}
                    setIsOpen={setAttachmentsDropdownOpen}
                    userAttachments={userAttachments}
                    selectedAttachments={selectedAttachments}
                    onAddAttachment={addAttachment}
                  />
                </div>

                <div className="msg-selected msg-selected--fill">
                  <div className="msg-chips" aria-label="Selected attachments">
                    {selectedAttachments.length === 0 ? (
                      <span className="msg-hint">
                        {t("file.onScreenMessage.noAttachments")}
                      </span>
                    ) : (
                      selectedAttachments.map((a) => {
                        const fileName =
                          (a as any)?.s3Key?.split("/").pop?.() ?? "";
                        const fileType = (a as any)?.type || "";
                        const Icon = getFileTypeIcon(fileType);
                        const label =
                          (a as any)?.name ||
                          (a as any)?.fileName ||
                          fileName ||
                          (a as any)?.title ||
                          (a as any)?.key ||
                          a.id;

                        return (
                          <div key={a.id} className="msg-chip btn--dark-green">
                            <Icon
                              className="msg-chip__icon"
                              aria-hidden="true"
                            />
                            <span className="msg-chip__text">{label}</span>
                            <button
                              id={`attachment-${a.id}-remove-btn`}
                              type="button"
                              className="msg-chip__remove"
                              onClick={() => removeAttachment(a.id)}
                              aria-label={`${t("common.remove")} ${label}`}
                              title={t("common.remove")}
                              disabled={controlsDisabled}
                            >
                              &times;
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`msg-panel${fieldErrors.deliveryType || fieldErrors.sendOn ? " msg-field--error" : ""}`}
            >
              <div className="msg-panel__header">
                <div className="msg-field__labelText">
                  {t("message.tableColumn.scheduledDate")}
                </div>
              </div>

              <div className="msg-delivery">
                <button
                  id="eol-button"
                  type="button"
                  className={`delivery-option delivery-option--eol ${deliveryType === "eol" ? "is-active" : ""}`}
                  aria-pressed={deliveryType === "eol"}
                  onClick={() => {
                    draftFieldsTouchedRef.current = true;
                    setDeliveryType((prev) =>
                      prev === "eol" ? "none" : "eol",
                    );
                    setFormError("");
                    if (fieldErrors.deliveryType || fieldErrors.sendOn) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        deliveryType: undefined,
                        sendOn: undefined,
                      }));
                    }
                  }}
                  disabled={controlsDisabled}
                >
                  <div className="delivery-option__icon" aria-hidden="true" />
                  <div className="delivery-option__label">
                    {t("common.endOfLife")}
                  </div>
                </button>

                <button
                  id="scheduled-button"
                  type="button"
                  className={`delivery-option delivery-option--scheduled ${
                    deliveryType === "scheduled" ? "is-active" : ""
                  }`}
                  aria-pressed={deliveryType === "scheduled"}
                  onClick={() => {
                    draftFieldsTouchedRef.current = true;
                    setDeliveryType((prev) =>
                      prev === "scheduled" ? "none" : "scheduled",
                    );
                    setFormError("");
                    if (fieldErrors.deliveryType || fieldErrors.sendOn) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        deliveryType: undefined,
                        sendOn: undefined,
                      }));
                    }
                  }}
                  disabled={controlsDisabled}
                >
                  <div className="delivery-option__icon" aria-hidden="true" />
                  <div className="delivery-option__label">
                    {t("common.fixedDate")}
                  </div>
                </button>
              </div>

              <div className="msg-scheduleHelp">
                <div
                  id={scheduleHelpId}
                  className={`schedule-help${fieldErrors.deliveryType ? " schedule-help--error" : ""}`}
                >
                  {fieldErrors.deliveryType ? fieldErrors.deliveryType : null}
                  {!fieldErrors.deliveryType &&
                    deliveryType === "eol" &&
                    t("message.onScreenMessage.sentEol")}
                  {!fieldErrors.deliveryType &&
                    deliveryType === "scheduled" &&
                    sendOn === "" &&
                    t("message.onScreenMessage.selectDateTime")}
                  {!fieldErrors.deliveryType &&
                    deliveryType === "none" &&
                    t("message.onScreenMessage.selectMethod")}
                  {!fieldErrors.deliveryType &&
                    deliveryType === "scheduled" &&
                    sendOn !== "" && (
                      <>
                        {`${t("message.onScreenMessage.sentOn")} `}
                        <span className="schedule-preview">
                          {convertUtcToLocal(
                            scheduledPreviewValue,
                            "ShowDayLong",
                          )}
                        </span>
                      </>
                    )}
                </div>
              </div>

              <div className="schedule-input-row">
                {deliveryType === "scheduled" && (
                  <>
                    <label
                      className="schedule-label schedule-label--date"
                      htmlFor="schedule-input"
                    >
                      <input
                        id="schedule-input"
                        className={`schedule-input${fieldErrors.sendOn ? " schedule-input--error" : ""}`}
                        type="date"
                        value={sendOn}
                        min={minimumScheduledDate}
                        aria-label={t("message.tableColumn.scheduledDate")}
                        aria-invalid={!!fieldErrors.sendOn}
                        aria-describedby={
                          fieldErrors.sendOn
                            ? `${scheduleHelpId} ${scheduleErrorId}`
                            : scheduleHelpId
                        }
                        onChange={(e) => {
                          draftFieldsTouchedRef.current = true;
                          setSendOn(e.target.value);
                          setFormError("");
                          if (fieldErrors.sendOn)
                            setFieldErrors((prev) => ({
                              ...prev,
                              sendOn: undefined,
                            }));
                        }}
                        disabled={
                          controlsDisabled || deliveryType !== "scheduled"
                        }
                      />
                    </label>

                    <label
                      className="schedule-label schedule-label--time"
                      htmlFor="schedule-time-slot"
                    >
                      <select
                        id="schedule-time-slot"
                        className="schedule-input schedule-select"
                        value={scheduledTimeSlot}
                        aria-label={t("message.tableColumn.scheduledTime")}
                        aria-describedby={
                          fieldErrors.sendOn
                            ? `${scheduleHelpId} ${scheduleErrorId}`
                            : scheduleHelpId
                        }
                        onChange={(e) => {
                          draftFieldsTouchedRef.current = true;
                          setScheduledTimeSlot(
                            e.target.value as FixedScheduledUtcSlot,
                          );
                        }}
                        disabled={
                          controlsDisabled || deliveryType !== "scheduled"
                        }
                      >
                        {FIXED_SCHEDULED_UTC_SLOTS.map((slot) => (
                          <option key={slot} value={slot}>
                            {getScheduledSlotLocalLabel(slot, sendOn)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
                {fieldErrors.sendOn ? (
                  <p
                    id={scheduleErrorId}
                    className="schedule-input-error"
                    aria-live="polite"
                  >
                    {fieldErrors.sendOn}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="delete-wrapper">
          <button
            id="delete-btn"
            type="button"
            className="btn btn--transparent-no-move message-delete-btn"
            onClick={handleDeleteMessage}
            disabled={deleteButtonDisabled}
          >
            <div className="icon-trash" />
            {t("message.deleteMessage")}
          </button>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isPromptOpen}
        title={t("errorMessage.unsavedChanges")}
        message={t("errorMessage.unsavedChangesPrompt")}
        primaryLabel={t("common.discardChanges")}
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleConfirmDiscardChanges}
        onSecondary={cancelNavigation}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        title={t("message.deleteMessage")}
        message={t("message.onScreenMessage.deleteMessageConfirm")}
        primaryLabel={isDeleting ? t("common.deleting") : t("common.delete")}
        secondaryLabel={t("common.keep")}
        onPrimary={handleConfirmDeleteMessage}
        onSecondary={handleKeepDeleteMessage}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
        primaryDisabled={isDeleting}
        secondaryDisabled={isDeleting}
      />
      <ConfirmationModal
        isOpen={isDeleteBlockedModalOpen}
        title={t("message.deleteMessage")}
        message={t("message.onScreenMessage.deleteMessageRemoveRecipients")}
        primaryLabel={t("common.ok")}
        onPrimary={handleDismissDeleteBlockedModal}
        primaryButtonClassName="btn btn--orange btn--size-md"
      />
      <ConfirmationModal
        isOpen={isDeleteSuccessModalOpen}
        title={t("message.deleteMessage")}
        message={t("message.onScreenMessage.deleteMessageSuccess")}
        primaryLabel={t("common.ok")}
        onPrimary={handleAcknowledgeDeleteSuccess}
        primaryButtonClassName="btn btn--orange btn--size-md"
      />
      <ConfirmationModal
        isOpen={isSaveResultModalOpen}
        title={saveResultTitle}
        message={saveResultMessage}
        primaryLabel={
          saveShouldReturnToPanel ? t("common.finishEdit") : t("common.close")
        }
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleCloseAfterSave}
        onSecondary={handleContinueEditingAfterSave}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
    </div>
  );
}

export default MessageEdit;
